import { Link } from 'react-router-dom';
import { FileText, ArrowLeft, Shield, Lock, CreditCard, Database, Users, Clock, Globe, UserCheck, RefreshCw, Mail } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const lastUpdated = "March 8, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center space-x-2">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-primary">FixResume24</span>
          </Link>
          <Link 
            to="/login" 
            className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-2">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last Updated: {lastUpdated}
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-slate-600 leading-relaxed">
              Welcome to FixResume24 ("we", "our", or "the Service"). We respect your privacy and are 
              committed to protecting your personal information. This Privacy Policy explains what information 
              we collect, how we use it, and how we protect it when you use our website and services.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Database className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">1. Information We Collect</h2>
            </div>
            <div className="pl-13 space-y-4 text-slate-600">
              <p>When you use our platform, we may collect the following information:</p>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-primary mb-2">Account Information</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Login provider (Google authentication)</li>
                </ul>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-primary mb-2">Uploaded Resume Files</h3>
                <p className="text-sm">
                  When you upload a resume to use our formatting or template services, the file is 
                  temporarily processed by our system to generate the formatted resume. 
                  <strong className="text-primary"> Resume data is stored in session only and is never permanently saved to our database.</strong>
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-semibold text-primary mb-2">Usage Information</h3>
                <p className="text-sm mb-2">We may collect limited usage data such as:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Browser type</li>
                  <li>Device information</li>
                  <li>Pages visited</li>
                  <li>Feature usage</li>
                </ul>
                <p className="text-sm mt-2">This helps us improve the service.</p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">2. How We Use Your Information</h2>
            </div>
            <div className="pl-13 text-slate-600">
              <p className="mb-3">We use collected information for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>To process and format resumes using our templates</li>
                <li>To provide resume improvement features</li>
                <li>To allow users to download formatted resumes</li>
                <li>To manage user accounts</li>
                <li>To process premium feature payments</li>
                <li>To improve application performance and user experience</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">3. Resume File Processing</h2>
            </div>
            <div className="pl-13 text-slate-600 space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-medium text-green-800 mb-2">Your Privacy is Protected</p>
                <ul className="space-y-2 text-sm text-green-700">
                  <li className="flex items-start gap-2">
                    <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Uploaded resumes are processed automatically by our system</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span><strong>We do not manually review or share resume content with third parties</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Resume data is stored in session only (2 hours) and automatically deleted</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Database className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Resume content is never permanently stored in our database</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4 - Payment */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-amber-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">4. Payment Information</h2>
            </div>
            <div className="pl-13 text-slate-600 space-y-3">
              <p>
                If you purchase premium features, payments are processed through a third-party payment 
                gateway (<strong>Razorpay</strong>).
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-medium text-amber-800 mb-2">We do NOT store:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-amber-700">
                  <li>Credit card numbers</li>
                  <li>Debit card numbers</li>
                  <li>UPI credentials</li>
                  <li>Banking details</li>
                </ul>
                <p className="text-sm text-amber-700 mt-2">
                  Payment processing is handled entirely by Razorpay's secure payment infrastructure.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 - Security */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <Lock className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">5. Data Security</h2>
            </div>
            <div className="pl-13 text-slate-600 space-y-3">
              <p>We implement reasonable security measures to protect user information, including:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>HTTPS encryption for all data transmission</li>
                <li>Secure session-based storage of uploaded files</li>
                <li>Restricted system access</li>
                <li>Protection of API keys and credentials</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                However, no system can guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Section 6 - Sharing */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                <Globe className="h-5 w-5 text-cyan-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">6. Data Sharing</h2>
            </div>
            <div className="pl-13 text-slate-600 space-y-3">
              <p className="font-medium text-primary">We do not sell, trade, or rent user personal information.</p>
              <p>We may share limited data only with essential service providers such as:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Payment processors (Razorpay)</li>
                <li>Cloud hosting services</li>
                <li>Analytics tools</li>
              </ul>
              <p className="text-sm">These providers only access the information required to perform their services.</p>
            </div>
          </section>

          {/* Section 7 - Retention */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Clock className="h-5 w-5 text-indigo-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">7. Data Retention</h2>
            </div>
            <div className="pl-13 text-slate-600 space-y-3">
              <ul className="list-disc list-inside space-y-2">
                <li>Uploaded resumes are stored in session only (maximum 2 hours) and automatically deleted</li>
                <li>User account information is retained while your account is active</li>
                <li>Users may request deletion of their data by contacting us</li>
              </ul>
            </div>
          </section>

          {/* Section 8 - Third Party */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center">
                <Globe className="h-5 w-5 text-pink-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">8. Third-Party Services</h2>
            </div>
            <div className="pl-13 text-slate-600">
              <p>
                Our platform may use third-party services such as payment processors or analytics providers. 
                These services have their own privacy policies and we encourage you to review them.
              </p>
            </div>
          </section>

          {/* Section 9 - Rights */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-teal-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">9. Your Rights</h2>
            </div>
            <div className="pl-13 text-slate-600">
              <p className="mb-3">You may:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Request deletion of your data</li>
                <li>Request access to stored data</li>
                <li>Stop using the service at any time</li>
              </ul>
              <p className="mt-3">For such requests, please contact us using the information below.</p>
            </div>
          </section>

          {/* Section 10 - Changes */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">10. Changes to This Policy</h2>
            </div>
            <div className="pl-13 text-slate-600">
              <p>
                We may update this Privacy Policy occasionally. Updated versions will be posted on this page 
                with the revised date. We encourage you to review this page periodically for any changes.
              </p>
            </div>
          </section>

          {/* Section 11 - Contact */}
          <section className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-slate-600" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-primary">11. Contact Information</h2>
            </div>
            <div className="pl-13 text-slate-600">
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mt-3">
                <p className="font-medium text-primary">FixResume24 Support</p>
                <p className="text-sm">Email: support@fixresume24.com</p>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-slate-100 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs text-muted-foreground">
            © 2026 FixResume24. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
